import React from 'react';
import { motion } from 'framer-motion';

interface InlineLoaderProps {
  message?: string;
  variant?: 'default' | 'campaign-cards';
  count?: number;
}

const InlineLoader: React.FC<InlineLoaderProps> = ({ 
  message = 'Loading...', 
  variant = 'default',
  count = 4 
}) => {
  // Campaign Card Skeleton
  if (variant === 'campaign-cards') {
    return (
      <div className="flex gap-6 overflow-hidden">
        {Array.from({ length: count }).map((_, index) => (
          <motion.div
            key={index}
            className="flex-shrink-0 w-[280px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="bg-[#2a2a2a] rounded-2xl overflow-hidden border border-gray-700/50">
              {/* Image Skeleton */}
              <div className="relative h-[200px] bg-gradient-to-br from-gray-700/50 to-gray-800/50 overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-600/20 to-transparent"
                  animate={{
                    x: ['-100%', '100%']
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "linear",
                    delay: index * 0.2
                  }}
                />
              </div>
              
              {/* Content Skeleton */}
              <div className="p-4 space-y-3">
                {/* Title */}
                <div className="h-5 bg-gray-700/50 rounded-lg w-3/4 overflow-hidden relative">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"
                    animate={{
                      x: ['-100%', '100%']
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                      delay: index * 0.2 + 0.1
                    }}
                  />
                </div>
                
                <div className="h-4 bg-gray-700/50 rounded-lg w-1/2 overflow-hidden relative">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"
                    animate={{
                      x: ['-100%', '100%']
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                      delay: index * 0.2 + 0.2
                    }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    );
  }

  // Default Sparkle Loader
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 rounded-2xl">
      {/* Sparkle Animation */}
      <div className="relative mb-4 w-16 h-16 flex items-center justify-center">
        {/* Center Sparkle */}
        <motion.svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          className="text-brand-primary absolute"
          animate={{
            opacity: [0.5, 1, 0.5],
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <path
            d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"
            fill="currentColor"
          />
        </motion.svg>
        
        {/* Top Right Sparkle */}
        <motion.svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          className="text-orange-400 absolute top-0 right-0"
          animate={{
            opacity: [0.3, 1, 0.3],
            scale: [0.8, 1.1, 0.8],
            rotate: [0, -90, -180],
          }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.3
          }}
        >
          <path
            d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"
            fill="currentColor"
          />
        </motion.svg>
        
        {/* Bottom Left Sparkle */}
        <motion.svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          className="text-orange-500 absolute bottom-0 left-0"
          animate={{
            opacity: [0.4, 1, 0.4],
            scale: [0.9, 1.15, 0.9],
            rotate: [0, 120, 240],
          }}
          transition={{
            duration: 2.2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.6
          }}
        >
          <path
            d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"
            fill="currentColor"
          />
        </motion.svg>
      </div>
      
      <p className="text-sm text-gray-400 italic">{message}</p>
    </div>
  );
};

export default InlineLoader;
